import React, { useState, useEffect, useCallback } from 'react';
import { Card as MuiCard, Typography, Stepper, Step, StepLabel, Button } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { setupCommunity } from '../../redux/actions/auth';
import { ISelfServiceSetupForm } from '../../types/auth';
import AccountStep from './AccountStep';
import OrganizationStep from './OrganizationStep';
import CommunityStep from './CommunityStep';
import ChannelsStep, { DEFAULT_CHANNELS } from './ChannelsStep';
import { useSetupStyles } from './style';

const STEPS = ['Account', 'Organization', 'Community', 'Channels'];
const STORAGE_KEY = 'setupWizardDraft';

// Fields to validate per step
const STEP_FIELDS: string[][] = [
  ['name', 'lastName', 'email', 'password'],
  ['organizationName'],
  ['community.communityName', 'community.communityType'],
  [],
];

// All saveable field names (exclude password for security)
const PERSISTED_FIELDS = [
  'name', 'lastName', 'email', 'phoneNumber',
  'organizationName',
  'community.communityName', 'community.communityType', 'community.about',
];

function loadDraft(): { fields: Record<string, string>; channels: any[] | null; step: number } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { fields: {}, channels: null, step: 0 };
}

function saveDraft(fields: Record<string, string>, channels: any[], step: number) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ fields, channels, step }));
  } catch { /* ignore */ }
}

function clearDraft() {
  localStorage.removeItem(STORAGE_KEY);
}

export default function SetupWizard() {
  const classes = useSetupStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const draft = loadDraft();
  const [activeStep, setActiveStep] = useState(draft.step || 0);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState(
    draft.channels || DEFAULT_CHANNELS.map((ch) => ({ ...ch }))
  );

  const { control, handleSubmit, errors, trigger, setValue, getValues } = useForm({
    mode: 'onBlur',
    defaultValues: draft.fields,
  });

  // Restore saved field values into the form on mount
  useEffect(() => {
    if (draft.fields) {
      Object.entries(draft.fields).forEach(([key, val]) => {
        if (val) setValue(key as any, val);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist current form state whenever user navigates between steps
  const persistCurrentState = useCallback((step: number) => {
    const values = getValues();
    const toSave: Record<string, string> = {};
    PERSISTED_FIELDS.forEach((f) => {
      const v = values[f];
      if (v) toSave[f] = v;
    });
    saveDraft(toSave, channels, step);
  }, [getValues, channels]);

  const handleNext = async () => {
    const fields = STEP_FIELDS[activeStep];
    if (fields.length > 0) {
      const valid = await trigger(fields as any);
      if (!valid) return;
    }
    const nextStep = activeStep + 1;
    persistCurrentState(nextStep);
    setActiveStep(nextStep);
  };

  const handleBack = () => {
    const prevStep = activeStep - 1;
    persistCurrentState(prevStep);
    setActiveStep(prevStep);
  };

  const onSubmit = async (data: any) => {
    setLoading(true);

    const enabledChannels = channels
      .filter((ch) => ch.enabled)
      .map((ch) => ({ channelName: ch.channelName, channelType: ch.channelType }));

    const communityName = data['community.communityName'] || data.community?.communityName;
    const communityType = data['community.communityType'] || data.community?.communityType;
    const communityAbout = data['community.about'] || data.community?.about;

    const payload: ISelfServiceSetupForm = {
      name: data.name,
      lastName: data.lastName,
      email: data.email,
      password: data.password,
      phoneNumber: data.phoneNumber || undefined,
      organizationName: data.organizationName,
      community: {
        communityName,
        communityType,
        about: communityAbout || undefined,
      },
      channels: enabledChannels.length > 0 ? enabledChannels : undefined,
      timezoneOffset: new Date().getTimezoneOffset(),
    };

    const result: any = await dispatch(setupCommunity(payload));
    setLoading(false);

    if (result?.success) {
      clearDraft();
      history.push('/user/dashboard');
    }
  };

  return (
    <div className={classes.container}>
      {loading ? (
        <div className="loader-container">
          <div className="loader" />
        </div>
      ) : (
        <MuiCard className={classes.card}>
          <Typography variant="h1" className={classes.title}>
            <img style={{ width: 120 }} src={logo} alt="Logo" />
          </Typography>
          <h1 className={classes.loginTitle}>Set Up My Community</h1>

          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div style={{ display: activeStep === 0 ? 'block' : 'none' }}>
              <AccountStep control={control} errors={errors} />
            </div>
            <div style={{ display: activeStep === 1 ? 'block' : 'none' }}>
              <OrganizationStep control={control} errors={errors} />
            </div>
            <div style={{ display: activeStep === 2 ? 'block' : 'none' }}>
              <CommunityStep control={control} errors={errors} />
            </div>
            <div style={{ display: activeStep === 3 ? 'block' : 'none' }}>
              <ChannelsStep channels={channels} setChannels={setChannels} />
            </div>

            <div className={classes.buttonRow}>
              {activeStep > 0 ? (
                <Button onClick={handleBack} className={classes.backBtn}>
                  Back
                </Button>
              ) : (
                <div />
              )}

              {activeStep < STEPS.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  className={classes.submitBtn}
                  style={{ marginBottom: 0 }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submitBtn}
                  style={{ marginBottom: 0 }}
                >
                  Create Community
                </Button>
              )}
            </div>
          </form>

          <Typography align="center" className={classes.paragraphText} style={{ marginTop: 16 }}>
            Already have an account?{' '}
            <Link to="/login" className={classes.singupLink}>
              Log in
            </Link>
          </Typography>
        </MuiCard>
      )}
    </div>
  );
}
